import { Select, NativeSelect, ComboboxData } from '@mantine/core';
import { ReactNode } from 'react';
import { useIsBottomSheet } from './ResponsiveDrawer';

type ResponsiveSelectProps = {
  data: ComboboxData;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  label?: ReactNode;
  placeholder?: string;
  clearable?: boolean;
  required?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  size?: string;
  style?: React.CSSProperties;
  renderOption?: React.ComponentProps<typeof Select>['renderOption'];
};

/**
 * A single-choice select that renders the platform's native picker on
 * bottom-sheet viewports and Mantine's Select elsewhere.
 *
 * Inside the mobile bottom sheet (see ResponsiveDrawer), Mantine's floating
 * dropdown gets mispositioned past the end of the document, which grows the
 * mobile layout viewport and shoves the sheet around. The native picker is
 * browser chrome, so nothing can misposition, and it is the better mobile
 * input anyway.
 *
 * Native mode differences: no search, and `placeholder`/`clearable` are
 * emulated with an empty-value option at the top of the list.
 */
export const ResponsiveSelect = ({
  data,
  value,
  onChange,
  label,
  placeholder,
  clearable,
  required,
  disabled,
  searchable,
  size,
  style,
  renderOption,
}: ResponsiveSelectProps) => {
  const isBottomSheet = useIsBottomSheet();

  if (!isBottomSheet) {
    return (
      <Select
        data={data}
        value={value ?? null}
        onChange={onChange}
        label={label}
        placeholder={placeholder}
        clearable={clearable}
        required={required}
        disabled={disabled}
        searchable={searchable}
        size={size}
        style={style}
        renderOption={renderOption}
      />
    );
  }

  // Native select has no placeholder or clear affordance: emulate both with
  // an empty option, kept selectable when the field is clearable/optional.
  const showEmptyOption = !!placeholder && (clearable || !required || !value);
  const nativeData: ComboboxData = showEmptyOption
    ? [{ value: '', label: placeholder!, disabled: required && !clearable }, ...(data as any[])]
    : data;

  return (
    <NativeSelect
      data={nativeData}
      value={value ?? ''}
      onChange={(e) => onChange(e.currentTarget.value || null)}
      label={label}
      required={required}
      disabled={disabled}
      size={size}
      style={style}
    />
  );
};
